import React, { useCallback, useEffect, useRef, useState } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { memberActivityApi } from './services/api';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { TierProvider } from './context/TierContext';
import { useTier } from './context/TierContext';
import Sidebar from './components/Sidebar';
import HeaderProfile from './components/HeaderProfile';
import Dashboard from './components/Dashboard';
import DirectoryEnhanced from './components/DirectoryEnhanced';
import QuoteToolEnhanced from './components/QuoteToolEnhanced';
import Profile from './components/Profile';
import Upgrade from './components/Upgrade';
import Rewards from './components/Rewards';
import Colleagues from './components/Colleagues';
import Referrals from './components/Referrals';
import Analytics from './components/Analytics';
import EventConnect from './components/EventConnect';
import MemberChat from './components/MemberChat';
import { ChatWebSocketProvider } from './context/ChatWebSocketContext';
import Complaints from './components/Complaints';
import GrievanceRedressal from './components/GrievanceRedressal';
import NotificationsPage from './components/NotificationsPage';
import MergersAcquisitions from './components/MergersAcquisitions';
import TeamManagement from './components/TeamManagement';
import Login from './components/Login';
import BoltCargoRegistration from './components/BoltCargoRegistration';
import HomePage from './components/HomePage';
import FAQs from './components/FAQs';
import CompanyRegistration from './components/CompanyRegistration';
import RegistrationSuccess from './components/RegistrationSuccess';
import VerifyEmail from './components/VerifyEmail';
import ResetPassword from './components/ResetPassword';
import ForgotPassword from './components/ForgotPassword';
import UserRegistrationDetails from './components/UserRegistrationDetails';
import BankAccountNumberPrompt from './components/BankAccountNumberPrompt';
import ProfilePictureUploadDialog from './components/ProfilePictureUploadDialog';
import ShareSocialPage from './components/ShareSocialPage';
import AdminApp from './admin/AdminApp';
import MemberStories from './components/MemberStories';
import MemberLeaderboard from './components/MemberLeaderboard';
import PublicInquiry from './components/PublicInquiry';
import { companyRegistrationApi } from './services/api';

// Loading spinner component
const LoadingScreen = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
      <p className="mt-4 text-gray-600">Loading...</p>
    </div>
  </div>
);

// Protected route wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, member } = useAuth();
  const location = useLocation();
  const [registrationGate, setRegistrationGate] = useState({
    loading: true,
    hasSubmittedRegistration: false,
  });

  const isRegisteredUser = (member?.tier || '').toLowerCase() === 'registered user';
  const isInvitedTeamMember =
    member?.has_admin_rights === false ||
    member?.role === 'Company Member' ||
    Boolean(member?.invited_by);
  const registrationAllowedPaths = new Set([
    '/dashboard',
    '/profile',
    '/notifications',
    '/quotes',
    '/company-registration',
    '/my-company-registration',
  ]);
  const isRegistrationAllowedPath = registrationAllowedPaths.has(location.pathname);

  useEffect(() => {
    let cancelled = false;

    const checkCompanyRegistration = async () => {
      if (!isAuthenticated || !isRegisteredUser) {
        setRegistrationGate({ loading: false, hasSubmittedRegistration: true });
        return;
      }

      try {
        setRegistrationGate((prev) => ({ ...prev, loading: true }));
        const response = await companyRegistrationApi.getMyRegistration();
        if (!cancelled) {
          setRegistrationGate({
            loading: false,
            hasSubmittedRegistration: response.data?.exists === true,
          });
        }
      } catch (error) {
        if (!cancelled) {
          if (error.response?.status !== 404) {
            console.error('Failed to check company registration gate:', error);
          }
          setRegistrationGate({ loading: false, hasSubmittedRegistration: false });
        }
      }
    };

    checkCompanyRegistration();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, isRegisteredUser, member?.member_id]);

  if (loading || registrationGate.loading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  if (isRegisteredUser && !isInvitedTeamMember && !registrationGate.hasSubmittedRegistration && !isRegistrationAllowedPath) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Main layout with sidebar
const MainLayout = ({ children }) => {
  const { member, showProfilePicturePrompt, setShowProfilePicturePrompt } = useAuth();
  const location = useLocation();
  const [isNavigationOpen, setIsNavigationOpen] = useState(false);
  const navigationTriggerRef = useRef(null);
  const lastTrackedSectionRef = useRef('');

  useEffect(() => {
    const routes = [
      ['/dashboard', 'Dashboard'], ['/directory', 'Directory'], ['/upgrade', 'Membership'],
      ['/profile', 'Profile'], ['/quotes', 'Quote Tool'], ['/events', 'Event Connect'],
      ['/chat', 'Messages'], ['/notifications', 'Notifications'], ['/stories', 'Success Stories'],
      ['/leaderboard', 'Member Leaderboard'], ['/team', 'Team Management'],
      ['/colleagues', 'Company Branches'], ['/referrals', 'Refer Partner'], ['/analytics', 'Analytics'],
      ['/mergers-acquisitions', 'M&A'], ['/complaints', 'Complaints'], ['/rewards', 'Rewards'],
    ];
    const match = routes.find(([path]) => location.pathname === path || location.pathname.startsWith(`${path}/`));
    if (!member?.member_id || !match || lastTrackedSectionRef.current === match[1]) return;
    lastTrackedSectionRef.current = match[1];
    memberActivityApi.recordSectionView(match[1]).catch(() => {});
  }, [location.pathname, member?.member_id]);

  const restoreNavigationFocus = useCallback(() => {
    if (typeof window === 'undefined') return;

    window.requestAnimationFrame(() => {
      if (window.matchMedia?.('(min-width: 1280px)').matches) return;

      const openModal = document.querySelector(
        '[role="dialog"][aria-modal="true"]:not([data-state="closed"]), [role="alertdialog"][aria-modal="true"]:not([data-state="closed"])'
      );
      if (!openModal) {
        navigationTriggerRef.current?.focus();
      }
    });
  }, []);

  const closeNavigationForModal = useCallback(() => {
    setIsNavigationOpen(false);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;

    const desktopBreakpoint = window.matchMedia('(min-width: 1280px)');
    const closeAtDesktopBreakpoint = (event) => {
      if (event.matches) {
        setIsNavigationOpen(false);
      }
    };

    closeAtDesktopBreakpoint(desktopBreakpoint);

    if (desktopBreakpoint.addEventListener) {
      desktopBreakpoint.addEventListener('change', closeAtDesktopBreakpoint);
      return () => desktopBreakpoint.removeEventListener('change', closeAtDesktopBreakpoint);
    }

    desktopBreakpoint.addListener(closeAtDesktopBreakpoint);
    return () => desktopBreakpoint.removeListener(closeAtDesktopBreakpoint);
  }, []);

  useEffect(() => {
    if (showProfilePicturePrompt && !member?.profile_picture) {
      closeNavigationForModal();
    }
  }, [closeNavigationForModal, member?.profile_picture, showProfilePicturePrompt]);

  const closeNavigation = useCallback(({ restoreFocus = true } = {}) => {
    if (!isNavigationOpen) return;

    setIsNavigationOpen(false);
    if (restoreFocus) {
      restoreNavigationFocus();
    }
  }, [isNavigationOpen, restoreNavigationFocus]);

  return (
    <div className="flex min-h-[100dvh] bg-gray-50">
      <Sidebar
        isOpen={isNavigationOpen}
        onClose={closeNavigation}
        onRestoreFocus={restoreNavigationFocus}
      />
      <HeaderProfile
        onOpenNavigation={() => setIsNavigationOpen(true)}
        menuTriggerRef={navigationTriggerRef}
        isNavigationOpen={isNavigationOpen}
      />
      <main
        className="min-w-0 flex-1 overflow-y-auto pt-16 xl:ml-64"
        inert={isNavigationOpen || undefined}
        aria-hidden={isNavigationOpen || undefined}
      >
        <div className="min-w-0 p-4 sm:p-6 xl:p-8">
          {children}
        </div>
      </main>
      <BankAccountNumberPrompt
        onOpen={closeNavigationForModal}
        onDismiss={restoreNavigationFocus}
      />
      <ProfilePictureUploadDialog
        open={showProfilePicturePrompt && !member?.profile_picture}
        onOpenChange={(open) => {
          setShowProfilePicturePrompt(open);
          if (!open) restoreNavigationFocus();
        }}
      />
    </div>
  );
};

const PointsSystemRoute = ({ children }) => {
  const { pointsSystemEnabled, loading } = useTier();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!pointsSystemEnabled) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const getSafeReturnTo = (value) => {
  if (!value || typeof value !== 'string') return null;
  if (!value.startsWith('/') || value.startsWith('//')) return null;
  return value.startsWith('/quotes?inquiry=') ? value : null;
};

const AuthenticatedRouteRedirect = ({ fallback }) => {
  const location = useLocation();
  const queryReturnTo = getSafeReturnTo(
    new URLSearchParams(location.search).get('returnTo')
  );
  const storedReturnTo = getSafeReturnTo(sessionStorage.getItem('post_auth_return_to'));
  const destination = queryReturnTo || storedReturnTo || fallback;

  useEffect(() => {
    sessionStorage.removeItem('post_auth_return_to');
  }, []);

  return <Navigate to={destination} replace />;
};

// App routes component
const AppRoutes = () => {
  const { isAuthenticated, loading, login, member } = useAuth();
  const memberId = member?.member_id;
  const postAuthRoute = '/dashboard';
  const isBoltCargoKycDomain = window.location.hostname === 'kyc.boltcargo.com';

  if (loading) {
    return <LoadingScreen />;
  }

  const routes = (
    <Routes>
      {/* Public route - Login */}
      <Route
        path="/login"
        element={
          isAuthenticated ? (
            <AuthenticatedRouteRedirect fallback={postAuthRoute} />
          ) : (
            <Login onLogin={login} />
          )
        }
      />

      {/* Public route - User Registration (Create Account) - Step 1 of flow */}
      <Route
        path="/register"
        element={
          isAuthenticated ? (
            <AuthenticatedRouteRedirect fallback={postAuthRoute} />
          ) : (
            <Login onLogin={login} defaultTab="register" />
          )
        }
      />
      <Route
        path="/boltcargo-register"
        element={
          isAuthenticated ? (
            <AuthenticatedRouteRedirect fallback={postAuthRoute} />
          ) : (
            <BoltCargoRegistration />
          )
        }
      />
      {/* Contact / referral signup - same as register with ?ref= prefilled */}
      <Route
        path="/contact"
        element={
          isAuthenticated ? (
            <Navigate to={postAuthRoute} replace />
          ) : (
            <Login onLogin={login} defaultTab="register" />
          )
        }
      />
      <Route path="/registration-success" element={<RegistrationSuccess />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/faqs" element={<FAQs />} />
      <Route path="/share-social" element={<ShareSocialPage />} />
      <Route path="/public/inquiry/:shareToken" element={<PublicInquiry />} />

      {/* Landing page - HomePage when not authenticated, redirect to dashboard when authenticated */}
      <Route
        path="/"
        element={
          isAuthenticated ? (
            <Navigate to={postAuthRoute} replace />
          ) : isBoltCargoKycDomain ? (
            <BoltCargoRegistration />
          ) : (
            <HomePage />
          )
        }
      />
      <Route
        path="/app/mobile-restricted"
        element={<Navigate to={isAuthenticated ? '/dashboard' : '/login'} replace />}
      />
      {/* Protected routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <MainLayout><Dashboard /></MainLayout>
          </ProtectedRoute>
        }
      />
      {/* Company Registration - Full page without sidebar (opens from Register Company button) */}
      <Route
        path="/company-registration"
        element={
          <ProtectedRoute>
            <CompanyRegistration />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-company-registration"
        element={
          <ProtectedRoute>
            <MainLayout><UserRegistrationDetails /></MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/directory"
        element={
          <ProtectedRoute>
            <MainLayout><DirectoryEnhanced /></MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/quotes"
        element={
          <ProtectedRoute>
            <MainLayout><QuoteToolEnhanced /></MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/event-connect"
        element={
          <ProtectedRoute>
            <MainLayout><EventConnect /></MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/chat"
        element={
          <ProtectedRoute>
            <MainLayout>
              <div className="-m-4 h-[calc(100dvh-4rem)] flex flex-col overflow-hidden sm:-m-6 xl:-m-8">
                <MemberChat />
              </div>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <MainLayout><Profile /></MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/team"
        element={
          <ProtectedRoute>
            <MainLayout><TeamManagement /></MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/upgrade"
        element={
          <ProtectedRoute>
            <MainLayout><Upgrade /></MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/rewards"
        element={
          <ProtectedRoute>
            <PointsSystemRoute>
              <MainLayout><Rewards /></MainLayout>
            </PointsSystemRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/colleagues"
        element={
          <ProtectedRoute>
            <MainLayout><Colleagues /></MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/referrals"
        element={
          <ProtectedRoute>
            <MainLayout><Referrals /></MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics"
        element={
          <ProtectedRoute>
            <MainLayout><Analytics /></MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/mergers-acquisitions"
        element={
          <ProtectedRoute>
            <MainLayout><MergersAcquisitions /></MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/complaints"
        element={
          <ProtectedRoute>
            <MainLayout><Complaints /></MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/complaints/grievance-redressal"
        element={
          <ProtectedRoute>
            <MainLayout><GrievanceRedressal /></MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <MainLayout><NotificationsPage /></MainLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/stories" element={<ProtectedRoute><MainLayout><MemberStories /></MainLayout></ProtectedRoute>} />
      <Route path="/leaderboard" element={<ProtectedRoute><MainLayout><MemberLeaderboard /></MainLayout></ProtectedRoute>} />

      {/* Admin Portal Routes */}
      <Route path="/admin/*" element={<AdminApp />} />

      {/* Catch all - redirect to landing page or dashboard */}
      <Route
        path="*"
        element={
          isAuthenticated ? (
            <Navigate to={postAuthRoute} replace />
          ) : (
            <Navigate to="/" replace />
          )
        }
      />
    </Routes>
  );

  return isAuthenticated && memberId ? (
    <ChatWebSocketProvider memberId={memberId}>
      {routes}
    </ChatWebSocketProvider>
  ) : (
    routes
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <TierProvider>
            <AppRoutes />
          </TierProvider>
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
